import { useMemo } from "react";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
  priority: 'high' | 'medium' | 'low';
}

const passwordRequirements: PasswordRequirement[] = [
  {
    label: "At least 8 characters",
    test: (password) => password.length >= 8,
    priority: 'high'
  },
  {
    label: "Contains uppercase letter",
    test: (password) => /[A-Z]/.test(password),
    priority: 'high'
  },
  {
    label: "Contains lowercase letter", 
    test: (password) => /[a-z]/.test(password),
    priority: 'high'
  },
  {
    label: "Contains a number",
    test: (password) => /\d/.test(password),
    priority: 'medium'
  },
  {
    label: "Contains special character (!@#$%^&*)",
    test: (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    priority: 'medium'
  },
  {
    label: "No common patterns (123, abc, qwerty)",
    test: (password) => {
      const commonPatterns = ['123', 'abc', 'qwerty', 'password', 'admin'];
      return !commonPatterns.some(pattern => 
        password.toLowerCase().includes(pattern)
      );
    },
    priority: 'low'
  }
];

export const PasswordStrengthMeter = ({ password, className = "" }: PasswordStrengthMeterProps) => {
  const { strength, score, requirements } = useMemo(() => {
    const passedRequirements = passwordRequirements.filter(req => req.test(password));
    const highPriorityPassed = passedRequirements.filter(req => req.priority === 'high').length;
    const mediumPriorityPassed = passedRequirements.filter(req => req.priority === 'medium').length;
    
    let score = 0;
    let strength = 'Very Weak';
    let color = 'text-red-600';
    
    if (password.length === 0) {
      strength = '';
      color = 'text-gray-400';
    } else if (highPriorityPassed === 3 && mediumPriorityPassed >= 1) {
      if (passedRequirements.length >= 5) {
        strength = 'Very Strong';
        color = 'text-green-600';
        score = 100;
      } else {
        strength = 'Strong';
        color = 'text-green-500';
        score = 80;
      }
    } else if (highPriorityPassed >= 2) {
      strength = 'Medium';
      color = 'text-yellow-600';
      score = 60;
    } else if (highPriorityPassed >= 1) {
      strength = 'Weak';
      color = 'text-orange-600';
      score = 40;
    } else {
      strength = 'Very Weak';
      color = 'text-red-600';
      score = 20;
    }
    
    return { 
      strength, 
      score, 
      requirements: passwordRequirements.map(req => ({
        ...req,
        passed: req.test(password)
      })),
      color
    };
  }, [password]);

  if (!password) return null;

  const getRequirementIcon = (passed: boolean, priority: string) => {
    if (passed) {
      return <CheckCircle className="h-3 w-3 text-green-600" />;
    } else if (priority === 'high') {
      return <XCircle className="h-3 w-3 text-red-500" />;
    } else {
      return <AlertCircle className="h-3 w-3 text-gray-400" />;
    }
  };

  const isStrong = score >= 80;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Strength indicator */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium text-gray-600">Password Strength</span>
          <span className={`text-xs font-medium ${strength ? requirements.find(r => r.passed)?.test ? 'text-gray-700' : 'text-gray-400' : ''}`}>
            {strength}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              score >= 80 ? 'bg-green-500' : 
              score >= 60 ? 'bg-yellow-500' : 
              score >= 40 ? 'bg-orange-500' : 
              'bg-red-500'
            }`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-600 mb-2">Password Requirements:</p>
        {requirements.map((req, index) => (
          <div key={index} className="flex items-center gap-2">
            {getRequirementIcon(req.passed, req.priority)}
            <span className={`text-xs ${
              req.passed ? 'text-green-700' : 
              req.priority === 'high' ? 'text-gray-700' : 'text-gray-500'
            }`}>
              {req.label}
            </span>
          </div>
        ))}
      </div>

      {/* Security tip */}
      {isStrong && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">
              Great! Your password is strong and secure.
            </span>
          </div>
        </div>
      )}

      {!isStrong && password.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <span className="text-xs text-amber-700">
              Consider a stronger password for better security.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const isPasswordStrong = (password: string): boolean => {
  const highPriorityTests = [
    (p: string) => p.length >= 8,
    (p: string) => /[A-Z]/.test(p),
    (p: string) => /[a-z]/.test(p)
  ];
  
  const mediumPriorityTests = [
    (p: string) => /\d/.test(p),
    (p: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p)
  ];
  
  const highPassed = highPriorityTests.filter(test => test(password)).length;
  const mediumPassed = mediumPriorityTests.filter(test => test(password)).length;
  
  return highPassed === 3 && mediumPassed >= 1;
};